import React, { useState } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import { updateApplicationRoundResult, fetchApplicationRounds } from "../../../../core/data/redux/recruitment/thunks";
import type { ApplicationRound } from "../../../../core/data/redux/recruitment/types";
import { ROUND_STATUS_COLORS } from "../../constants/recruitment.constants";

interface Props {
  round: ApplicationRound;
  applicationId: number;
  onClose: () => void;
}

const DECISION_OPTIONS = [
  { value: "passed", label: "Passed", icon: "ti-check", color: "#00C851" },
  { value: "failed", label: "Failed", icon: "ti-x", color: "#E74C3C" },
  { value: "skipped", label: "Skipped", icon: "ti-arrow-right", color: "#95A5A6" },
  { value: "no_show", label: "No Show", icon: "ti-user-off", color: "#E67E22" },
];

const RoundDecisionModal: React.FC<Props> = ({ round, applicationId, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [status, setStatus] = useState<string>("");
  const [score, setScore] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [override, setOverride] = useState(false);
  const [notify, setNotify] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!status) return;
    setSubmitting(true);
    try {
      await dispatch(
        updateApplicationRoundResult({
          applicationRoundId: round.id,
          roundStatus: status,
          score: score ? parseFloat(score) : undefined,
          notes: notes || undefined,
          resultOverride: override,
          sendNotification: notify,
        }),
      ).unwrap();
      // Refresh the pipeline
      dispatch(fetchApplicationRounds(applicationId));
      onClose();
    } catch (err) {
      console.error("Failed to update round:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(4px)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          overflow: "hidden",
          animation: "slideUp 0.25s ease",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h6 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
              <i className="ti ti-gavel me-2" style={{ color: "#6366f1" }} />
              Round Decision
            </h6>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              {round.round_name} · Round {round.stage_order}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 18,
              color: "#94a3b8",
              padding: 4,
            }}
          >
            <i className="ti ti-x" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px" }}>
          {/* Status selection */}
          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8, display: "block" }}>
            Decision *
          </label>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {DECISION_OPTIONS.map((opt) => {
              const selected = status === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setStatus(opt.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "6px 14px",
                    borderRadius: 8,
                    border: `1.5px solid ${selected ? opt.color : "#e2e8f0"}`,
                    background: selected ? `${opt.color}14` : "#fff",
                    color: selected ? opt.color : "#64748b",
                    fontSize: 12,
                    fontWeight: selected ? 700 : 500,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <i className={`ti ${opt.icon}`} style={{ fontSize: 13 }} />
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Score */}
          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4, display: "block" }}>
            Score (0-100) *
          </label>
          <input
            type="number"
            min={0}
            max={100}
            value={score}
            onChange={(e) => setScore(e.target.value)}
            placeholder="Optional"
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 12,
              marginBottom: 12,
              outline: "none",
            }}
          />

          {/* Notes */}
          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4, display: "block" }}>
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add decision notes…"
            rows={3}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 12,
              resize: "vertical",
              marginBottom: 12,
              outline: "none",
            }}
          />

          {/* Checkboxes */}
          <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#475569", marginBottom: 4 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
                style={{ accentColor: "#6366f1" }}
              />
              Override (force result)
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                style={{ accentColor: "#6366f1" }}
              />
              Notify candidate
            </label>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: "#fff",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              color: "#64748b",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!status || !score || submitting}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              border: "none",
              background: status ? ROUND_STATUS_COLORS[status] || "#6366f1" : "#cbd5e1",
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              cursor: status && score && !submitting ? "pointer" : "not-allowed",
              opacity: submitting || !score ? 0.6 : 1,
              transition: "all 0.15s",
            }}
          >
            {submitting ? "Saving…" : "Confirm Decision"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoundDecisionModal;
